// <pbr-trade-form data-endpoint="…" data-member-text="…" data-pending-text="…">
// Posts the trade application via fetch, shows inline field errors, and
// resizes image uploads so they stay under the endpoint's 4 MB cap (the
// platform answers oversize bodies with a CORS-less 413 we can't read).

const MAX_EDGE = 1600;
const MAX_BYTES = 4 * 1024 * 1024;
const SIZE_ERROR = 'File must be under 4 MB';

class PbrTradeForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.success = this.querySelector('.pbr-tf__success');
    this.errorBox = this.querySelector('.pbr-tf__error');
    if (!this.form || !this.success || !this.errorBox) return;

    const started = this.form.querySelector('[name="started_at"]');
    if (started) started.value = String(Date.now());
    this.errorId = `pbr-tf-err-${Math.random().toString(36).slice(2, 8)}`;
    this.form.addEventListener('submit', (e) => this.submit(e));

    // Custom upload control: mirror the chosen file name into the visible box.
    const fileInput = this.form.querySelector('[name="certificate"]');
    const fileName = this.form.querySelector('.pbr-tf__upload-name');
    if (fileInput && fileName) {
      fileInput.addEventListener('change', () => {
        const chosen = fileInput.files && fileInput.files[0];
        fileName.textContent = chosen ? chosen.name : fileName.dataset.placeholder || 'Upload...';
        fileName.classList.toggle('has-file', Boolean(chosen));
      });
    }
  }

  async submit(event) {
    event.preventDefault();
    this.clearErrors();

    const endpoint = this.dataset.endpoint;
    if (!endpoint) return this.showError('The application form is not configured yet.');

    // Native constraint validation first; the form is `novalidate` so we can
    // render the browser's messages inline in our own style.
    if (!this.form.checkValidity()) {
      this.form.querySelectorAll(':invalid').forEach((input) => {
        this.fieldError(input.name, input.validationMessage || 'Please fill in this field.');
      });
      return;
    }

    const button = this.form.querySelector('[type="submit"]');
    button.disabled = true;
    try {
      const body = new FormData(this.form);
      const file = body.get('certificate');
      if (file instanceof File && file.size > 0) {
        const prepared = await this.prepareFile(file);
        if (!prepared) return this.fieldError('certificate', SIZE_ERROR);
        body.set('certificate', prepared, prepared.name);
      } else {
        body.delete('certificate');
      }

      const res = await fetch(endpoint, { method: 'POST', body });
      const data = await res.json().catch(() => ({}));

      if (res.status === 400 && data.errors) {
        Object.entries(data.errors).forEach(([k, v]) => this.fieldError(k, v));
        return;
      }
      if (res.status === 429) return this.showError('Too many attempts. Please try again in a few minutes.');
      if (!res.ok) return this.showError('Something went wrong on our end. Please try again in a moment.');

      const statusText = { member: this.dataset.memberText, pending: this.dataset.pendingText }[data.status];
      if (statusText) this.success.querySelector('.pbr-tf__success-body').textContent = statusText;

      this.form.hidden = true;
      this.success.hidden = false;
      // Move focus to the heading so screen readers announce the outcome.
      const heading = this.success.querySelector('.pbr-tf__success-heading');
      heading?.focus({ preventScroll: true });
      this.success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      this.showError("We couldn't reach the server. Check your connection and try again — your answers are still here.");
    } finally {
      button.disabled = false;
    }
  }

  // Returns a File under the cap, or null when it can't be made to fit.
  // Non-images (PDFs) pass through; images larger than MAX_EDGE or over the
  // cap are redrawn as JPEG. Formats the browser can't decode (e.g. HEIC)
  // fall back to the original file if it's already small enough.
  async prepareFile(file) {
    const fits = (f) => (f.size <= MAX_BYTES ? f : null);
    if (!file.type.startsWith('image/')) return fits(file);
    try {
      // from-image keeps EXIF orientation so iPhone photos aren't rotated after resize.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
      if (scale === 1 && file.size <= MAX_BYTES) return file;

      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
      bitmap.close?.();

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      if (!blob) return fits(file);

      const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
      return fits(new File([blob], name, { type: 'image/jpeg' }));
    } catch {
      return fits(file);
    }
  }

  fieldError(name, message) {
    const input = this.form.querySelector(`[name="${CSS.escape(name)}"]`);
    const field = input?.closest('.pbr-tf__field');
    if (!field) return this.showError(message);

    const note = document.createElement('span');
    note.className = 'pbr-tf__field-error';
    note.id = `${this.errorId}-${name}`;
    note.textContent = message;

    field.classList.add('is-invalid');
    field.appendChild(note);
    input.setAttribute('aria-invalid', 'true');
    const described = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    described.push(note.id);
    input.setAttribute('aria-describedby', described.join(' '));

    // Only the first invalid field takes focus.
    if (!this.form.querySelector('.is-invalid :focus')) input.focus();
  }

  // The alert paragraph stays in the DOM; toggling text (not `hidden`) is
  // what makes role="alert" announce.
  showError(message) {
    this.errorBox.textContent = message;
  }

  clearErrors() {
    this.errorBox.textContent = '';
    this.form.querySelectorAll('.is-invalid').forEach((el) => el.classList.remove('is-invalid'));
    this.form.querySelectorAll('[aria-invalid="true"]').forEach((input) => {
      input.removeAttribute('aria-invalid');
      const kept = (input.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter((id) => id && !id.startsWith(this.errorId));
      if (kept.length) input.setAttribute('aria-describedby', kept.join(' '));
      else input.removeAttribute('aria-describedby');
    });
    this.form.querySelectorAll('.pbr-tf__field-error').forEach((el) => el.remove());
  }
}

if (!customElements.get('pbr-trade-form')) customElements.define('pbr-trade-form', PbrTradeForm);
